"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { MoreVerticalIcon } from "lucide-react";
import { toast } from "sonner";

import { parseChatConversationId } from "@/config/navigation";
import { cn } from "@/lib/utils";
import {
  archiveConversation,
  revalidateConversation,
  revalidateConversationsList,
  renameConversation,
} from "@/services/chat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from "@gogive/design-system";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@gogive/design-system/components/sidebar";

function conversationLabel(title: string | null): string {
  const trimmed = title?.trim();
  return trimmed ? trimmed : "New chat";
}

export function ChatConversationNavItem({
  id,
  title,
}: {
  id: string;
  title: string | null;
}) {
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const { isMobile } = useSidebar();
  const active = parseChatConversationId(pathname) === id;
  const href = `/chat/${id}`;
  const label = conversationLabel(title);

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(label);
  const [menuOpen, setMenuOpen] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (!isRenaming) {
      setRenameDraft(label);
    }
  }, [isRenaming, label]);

  useEffect(() => {
    if (!isRenaming) return;
    const frame = requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [isRenaming]);

  const startRename = useCallback(() => {
    setRenameDraft(label);
    setIsRenaming(true);
  }, [label]);

  const commitRename = useCallback(async () => {
    const trimmed = renameDraft.trim();
    const previous = label.trim();

    if (!trimmed) {
      setRenameDraft(label);
      setIsRenaming(false);
      return;
    }

    if (trimmed === previous) {
      setIsRenaming(false);
      return;
    }

    try {
      await renameConversation(id, trimmed);
      await Promise.all([
        revalidateConversationsList(),
        revalidateConversation(id),
      ]);
    } catch {
      toast.error("Could not rename conversation");
      setRenameDraft(label);
    } finally {
      setIsRenaming(false);
    }
  }, [id, label, renameDraft]);

  const handleRenameBlur = useCallback(() => {
    if (skipBlurSaveRef.current) {
      skipBlurSaveRef.current = false;
      setIsRenaming(false);
      setRenameDraft(label);
      return;
    }
    void commitRename();
  }, [commitRename, label]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveConversation(id);
      await revalidateConversationsList();
      if (parseChatConversationId(pathname) === id) {
        router.push("/chat");
      }
      toast.success("Conversation archived");
    } catch {
      toast.error("Could not archive conversation");
    }
  }, [id, pathname, router]);

  return (
    <SidebarMenuItem className="group/menu-item">
      {isRenaming ? (
        <SidebarMenuButton
          isActive={active}
          className="h-8 cursor-default pr-8 hover:bg-transparent"
          onClick={(event) => event.preventDefault()}
        >
          <Input
            ref={renameInputRef}
            value={renameDraft}
            onChange={(event) => setRenameDraft(event.target.value)}
            onBlur={handleRenameBlur}
            maxLength={255}
            className="h-7 min-w-0 border-0 bg-transparent px-1 shadow-none focus-visible:ring-1"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                skipBlurSaveRef.current = true;
                event.currentTarget.blur();
              }
            }}
            onClick={(event) => event.stopPropagation()}
          />
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton asChild isActive={active}>
          <Link
            href={href}
            className={cn("[&_[data-slot=icon]]:text-muted-foreground")}
          >
            <span className="truncate">{label}</span>
          </Link>
        </SidebarMenuButton>
      )}
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            showOnHover
            aria-label={`Actions for ${label}`}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreVerticalIcon />
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-48 rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align={isMobile ? "end" : "start"}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuItem
            icon="Pencil"
            onSelect={() => {
              setMenuOpen(false);
              startRename();
            }}
          >
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            icon="Archive"
            onSelect={() => {
              setMenuOpen(false);
              void handleArchive();
            }}
          >
            Archive
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
